import { Grid, Typography, useEventCallback } from '@mui/material';
import { useFormikContext } from 'formik';
import { memo, useCallback } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from 'front-office/constants/electoral-card-table-head-cells';
import { noop } from 'shell/utils/noop';
import {
  FiltersFormTableElectoralCard,
  TableElectoralCardBody,
} from 'front-office/view-models/electoral-card';
import { Nocp } from 'shell/utils/nocp';
import { base64ToBlob } from 'shell/utils/base64-to-blob';
import { downloadBlob } from 'front-office/utils/download-blob';
import { DateTime } from 'luxon';
import { __ELECTORAL_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseCsv } from 'shell/models/response-csv';
import { transformDataset } from 'front-office/utils/transformer-electoral-card';
import { CsvExtraction } from 'shell/components/shared/csv-extraction';

interface TableElectoralCardProps {
  data?: Array<TableElectoralCardBody>;
  count: number;
  getData: (values: FiltersFormTableElectoralCard) => Promise<void>;
}

const TableElectoralCardComponent = ({
  data,
  getData,
  count,
}: TableElectoralCardProps): React.ReactElement => {
  const formContext = useFormikContext<FiltersFormTableElectoralCard>();

  const handleChangePage = useEventCallback((newPage: number) => {
    getData({ ...formContext.values, page: newPage }).then(() => {
      formContext.setFieldValue('page', newPage);
    });
  });

  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    const newDataset = {
      ...formContext.values,
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    };

    getData(newDataset).then(() => {
      formContext.setValues(newDataset);
    });
  });

  const getCsv = useHttpClient<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>(
    `${__ELECTORAL_API_URL__}/tessere-elettorali/csv`,
    {
      transformer: (response) => response?.data,
    }
  );

  const toPrintCSV = useCallback(() => {
    const queryString = transformDataset(formContext.values);

    (
      getCsv({
        params: {},
        paramsSerializer: () => queryString,
      }) as ResponsePromise<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>
    ).then((response) => {
      const responseCasted = response as HttpResponse<ResponseCsv>;
      const csv = responseCasted?.payload?.contenutoCsv;
      if (csv) {
        // download csv
        const blob = base64ToBlob(csv);
        downloadBlob(blob, `export-csv-${DateTime.now().toFormat('ddMMyyHHmmss')}.csv`);
      }
    });
  }, [formContext.values, getCsv]);

  if (!data) {
    return <Nocp />;
  }

  return !data?.length ? (
    <>
      <Typography color="secondary.main">
        Nessun elemento trovato con la ricerca effettuata.
      </Typography>
    </>
  ) : (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <CsvExtraction onClick={toPrintCSV} title="Lista_Tessere" />
      </Grid>
      <Grid item xs={12}>
        <Table
          onSort={noop}
          noSort
          isServer
          head={headCells}
          orders={{}}
          page={formContext.values.page || 0}
          rowsPerPage={formContext.values.rowsPerPage}
          body={data || []}
          count={count || 0}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ariaLabel="Lista certificati"
          hideButtons
        />
      </Grid>
    </Grid>
  );
};

TableElectoralCardComponent.displayName = 'Table Print Receipt Pos';

const TableElectoralCard = memo(TableElectoralCardComponent);

export default TableElectoralCard;
